import { Link } from '@tanstack/react-router'
import { Check, ChevronsUpDown, LockIcon, Loader2Icon, PanelLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { IndividualType } from '@/hooks/useIndividuals'

// Small marker shown on an individual when its generated data is being
// regenerated (spinner) or new data is available to regenerate (dot).
const RegenIndicator = ({ regen }: { regen?: IndividualType['regen'] }) => {
  if (regen?.status === 'running') {
    return <Loader2Icon className="size-3.5 shrink-0 animate-spin text-current/70" />
  }
  if (regen?.stale) {
    return <span className="size-2 shrink-0 rounded-full bg-brand-funkia" />
  }
  return null
}

// Marker for an individual that isn't fully usable. Reads each row's OWN
// subscription rather than useSubscription(), which only knows the selected one —
// the point of this marker is to show the state of the individuals you'd switch to.
const SubscriptionIndicator = ({
  subscription,
}: {
  subscription?: IndividualType['subscription']
}) => {
  if (subscription?.status === 'expired') {
    return <LockIcon className="size-3.5 shrink-0 text-current/70" />
  }

  if (subscription?.status === 'trial' && subscription.documentsRemaining === 0) {
    return <span className="size-2 shrink-0 rounded-full bg-warning" />
  }

  return null
}

import FunkityLogo from '@/assets/icons/FunkityLogo'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { useIndividuals } from '@/hooks/useIndividuals'

import { Button } from './ui/button'

export const IndividualSwitcher = () => {
  const { individuals, selectedIndividual, selectedIndividualId, selectIndividual } =
    useIndividuals()
  const { t } = useTranslation()
  const { toggleSidebar } = useSidebar()

  return (
    <SidebarMenu>
      <div className="justify-between items-center flex pt-0 pb-3">
        <FunkityLogo className="h-6 text-brand-gran group-data-[collapsible=icon]:hidden" />
        <Button
          className="group-data-[collapsible=icon]:size-10"
          onClick={toggleSidebar}
          size="icon"
          variant="ghost"
        >
          <PanelLeft />
          <span className="sr-only">{t('global.toggleSidebar')}</span>
        </Button>
      </div>
      <SidebarMenuItem>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              className="bg-[#005c56] hover:bg-[#005c56] active:bg-[#005c56] text-sidebar-primary hover:text-sidebar-primary active:text-sidebar-primary"
              size="lg"
            >
              {selectedIndividual && (
                <div className="flex items-center gap-2 leading-none">
                  <span className="font-semibold text-base text-sidebar-primary">
                    <span className="group-data-[collapsible=icon]:hidden">
                      {selectedIndividual.firstName}
                    </span>
                    <span className="hidden group-data-[collapsible=icon]:block">
                      {selectedIndividual.firstName[0]}
                    </span>
                  </span>
                  <RegenIndicator regen={selectedIndividual.regen} />
                  <SubscriptionIndicator subscription={selectedIndividual.subscription} />
                </div>
              )}
              <ChevronsUpDown className="ml-auto group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width]">
            {individuals.map(individual => (
              <DropdownMenuItem
                key={individual.id}
                onSelect={() => selectIndividual(individual.id)}
              >
                {individual.firstName} {individual.lastName}{' '}
                <RegenIndicator regen={individual.regen} />
                <SubscriptionIndicator subscription={individual.subscription} />
                {individual.id === selectedIndividualId && <Check className="ml-auto" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem asChild className="text-text-secondary">
              <Link to="/add-individual">+ {t('navigation.individualPicker.createButton')}</Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
